import { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Check, ArrowRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_CONTENT = {
  club: {
    name: 'Just Dive Club',
    price: 'R1,000',
    period: '/ year',
    tagline: 'Most members recover the joining fee with their first purchase.',
    image_url: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?crop=entropy&cs=srgb&fm=jpg&q=85',
    features: [
      'UP TO 20% discount on all new gear',
      'Access to curated bundles',
      'Reduced training costs',
      'Member-only offers',
      'In-store fitting and support',
    ],
  },
  pro: {
    name: 'Just Dive Pro',
    price: 'R3,600',
    period: '/ year',
    price_note: '(R300/month × 12 months, paid upfront)',
    tagline: '12-month commitment. Club members only.',
    image_url: 'https://images.unsplash.com/photo-1544642058-84b73cf56a32?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHw0fHxzY3ViYSUyMGRpdmVyJTIwdW5kZXJ3YXRlciUyMG9jZWFufGVufDB8fHx8MTc2Nzg4MjA0N3ww&ixlib=rb-4.1.0&q=85',
    features: [
      'UP TO 25% discount on all new gear',
      'One annual dive getaway included',
      'Trip banking for up to 24 months',
      'Priority booking on trips',
      'Better rates on extensions',
      'Priority access to new gear',
    ],
  },
};

const MembershipPage = () => {
  const { user } = useAuth();
  const [content, setContent] = useState(DEFAULT_CONTENT);

  useEffect(() => {
    axios.get(`${API_URL}/membership-content`)
      .then((res) => {
        if (res?.data?.club && res?.data?.pro) setContent(res.data);
      })
      .catch(() => { /* keep defaults */ });
  }, []);

  const club = content.club || DEFAULT_CONTENT.club;
  const pro = content.pro || DEFAULT_CONTENT.pro;

  return (
    <div className="min-h-screen py-12 bg-[#FAFAFA]" data-testid="membership-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-[#0A2342] mb-4">
            {content.heading || DEFAULT_CONTENT.heading}
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {content.subheading || DEFAULT_CONTENT.subheading}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Club Membership */}
          <div className="relative overflow-hidden border-2 border-gray-200 hover:border-[#0A2342] transition-colors p-8 rounded-lg bg-white" data-testid="club-card">
            <div className="mb-6">
              <h3 className="text-3xl font-heading font-bold text-[#0A2342] mb-2">
                {club.name}
              </h3>
              <div className="flex items-baseline gap-2 mb-4">
                <span className="text-5xl font-mono-accent font-bold text-[#0A2342]">{club.price}</span>
                <span className="text-gray-600">{club.period}</span>
              </div>
              <p className="text-gray-600">{club.tagline}</p>
            </div>

            <div
              className="aspect-video mb-6 rounded-lg overflow-hidden"
              style={{
                backgroundImage: `url('${club.image_url}')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            />

            <ul className="space-y-3 mb-8">
              {(club.features || []).map((feature) => (
                <li key={`club-${feature}`} className="flex items-start gap-3">
                  <div className="w-5 h-5 bg-[#0A2342] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-white" strokeWidth={3} />
                  </div>
                  <span className="text-gray-700">{feature}</span>
                </li>
              ))}
            </ul>

            {user ? (
              user.membership_tier === 'club' || user.membership_tier === 'pro' ? (
                <Button disabled className="w-full" data-testid="club-active-btn">
                  Active Membership
                </Button>
              ) : (
                <Link to="/cart" state={{ membershipPurchase: 'club' }}>
                  <Button
                    className="w-full bg-[#0A2342] text-white hover:bg-[#0A2342]/90 shadow-lg hover:shadow-xl transition-all duration-300"
                    data-testid="club-join-btn"
                  >
                    Join Club
                    <ArrowRight className="ml-2 w-5 h-5" strokeWidth={1.5} />
                  </Button>
                </Link>
              )
            ) : (
              <Link to="/auth">
                <Button
                  className="w-full bg-[#0A2342] text-white hover:bg-[#0A2342]/90"
                  data-testid="club-signin-btn"
                >
                  Sign In to Join
                </Button>
              </Link>
            )}
          </div>

          {/* Pro Membership */}
          <div className="relative overflow-hidden border-2 border-[#FF6B35] hover:border-[#FF6B35] transition-colors p-8 rounded-lg bg-white" data-testid="pro-card">
            <div className="absolute top-0 right-0 bg-[#FF6B35] text-white px-4 py-1 text-sm font-semibold">
              BEST VALUE
            </div>

            <div className="mb-6 mt-4">
              <h3 className="text-3xl font-heading font-bold text-[#0A2342] mb-2">
                {pro.name}
              </h3>
              <div className="flex items-baseline gap-2 mb-2">
                <span className="text-5xl font-mono-accent font-bold text-[#0A2342]">{pro.price}</span>
                <span className="text-gray-600">{pro.period}</span>
              </div>
              {pro.price_note && (
                <p className="text-sm text-gray-500 mb-2">{pro.price_note}</p>
              )}
              <p className="text-gray-600">{pro.tagline}</p>
            </div>

            <div
              className="aspect-video mb-6 rounded-lg overflow-hidden"
              style={{
                backgroundImage: `url('${pro.image_url}')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            />

            <ul className="space-y-3 mb-8">
              {(pro.features || []).map((feature) => (
                <li key={`pro-${feature}`} className="flex items-start gap-3">
                  <div className="w-5 h-5 bg-[#FF6B35] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-white" strokeWidth={3} />
                  </div>
                  <span className="text-gray-700">{feature}</span>
                </li>
              ))}
            </ul>

            {user ? (
              user.membership_tier === 'pro' ? (
                <Button disabled className="w-full" data-testid="pro-active-btn">
                  Active Membership
                </Button>
              ) : user.membership_tier === 'club' ? (
                <Link to="/cart" state={{ membershipPurchase: 'pro' }}>
                  <Button
                    className="w-full bg-[#FF6B35] text-white hover:bg-[#FF6B35]/90 shadow-lg hover:shadow-xl transition-all duration-300"
                    data-testid="pro-upgrade-btn"
                  >
                    Upgrade to Pro
                    <ArrowRight className="ml-2 w-5 h-5" strokeWidth={1.5} />
                  </Button>
                </Link>
              ) : (
                <div className="text-center text-sm text-gray-500">
                  Join Club first to upgrade to Pro
                </div>
              )
            ) : (
              <Link to="/auth">
                <Button
                  className="w-full bg-[#FF6B35] text-white hover:bg-[#FF6B35]/90"
                  data-testid="pro-signin-btn"
                >
                  Sign In to Join
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MembershipPage;
